import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { Page } from '../types';
import { Markdown } from './Markdown';
import { motion } from 'motion/react';
import { ChevronLeft, Loader2, AlertCircle } from 'lucide-react';
import logo from '../assets/logo.png';

export const StaticPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pageId) return;

    setLoading(true);
    setError(null);

    const unsub = onSnapshot(doc(db, 'pages', pageId), (snap) => {
      if (snap.exists()) {
        setPage({ ...snap.data(), id: snap.id } as Page);
      } else {
        setError("Page not found.");
      }
      setLoading(false);
    }, (err) => {
      console.error("Error fetching page:", err);
      setError("Failed to load page content.");
      setLoading(false);
      handleFirestoreError(err, OperationType.GET, `pages/${pageId}`);
    });

    return () => unsub();
  }, [pageId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main flex flex-col items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-cat-overall animate-spin mb-4" />
        <p className="text-[0.65rem] tracking-widest uppercase text-text-muted">Loading content...</p>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main flex flex-col items-center justify-center p-8">
        <AlertCircle className="w-8 h-8 text-red-500 mb-4" />
        <p className="text-[0.7rem] tracking-widest uppercase text-text-main mb-6">{error || "Page not found"}</p>
        <Link to="/" className="text-[0.65rem] tracking-widest uppercase text-text-muted hover:text-text-main transition-colors flex items-center gap-2">
          <ChevronLeft size={14} /> Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main text-text-main flex flex-col items-center p-8 md:p-12">
      <header className="w-full max-w-3xl flex justify-between items-center mb-16">
        <Link to="/" className="flex items-center gap-4 group">
          <img src={logo} alt="Logo" className="w-8 h-8 opacity-80 group-hover:opacity-100 transition-opacity" referrerPolicy="no-referrer" />
          <h1 className="text-[0.75rem] font-light tracking-[0.35em] uppercase text-text-header group-hover:text-text-secondary transition-colors">THE INSOURCING WAY</h1>
        </Link>
        <Link to="/" className="text-[0.65rem] tracking-widest uppercase text-text-muted hover:text-text-main transition-colors flex items-center gap-2">
          <ChevronLeft size={14} /> Back
        </Link>
      </header>

      <motion.main 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl"
      >
        <h2 className="text-3xl md:text-4xl font-extralight text-cat-overall mb-12 tracking-tight">{page.title}</h2>
        
        <div className="prose prose-invert prose-sm max-w-none">
          <Markdown 
            content={page.content} 
            className="text-text-secondary leading-relaxed space-y-6"
          />
        </div>

        <div className="mt-24 pt-8 border-t border-border-main flex justify-between items-center">
          <p className="text-[0.6rem] tracking-widest uppercase text-text-muted">
            Last updated: {page.updatedAt?.toDate().toLocaleDateString()}
          </p>
          <Link to="/" className="text-[0.65rem] tracking-widest uppercase text-text-muted hover:text-text-main transition-colors">
            Back to Home
          </Link>
        </div>
      </motion.main>
    </div>
  );
};
